import type { SellerAnalysis, SAAgent } from "./sellerAnalysisTypes";

/** Canonical Alain profile — matches PitchAI production defaults. */
const ALAIN: SAAgent = {
  name: "Alain Onesti",
  title: "Licensed Real Estate Salesperson",
  brokerage: "Douglas Elliman",
  address: "575 Madison Avenue, New York, NY 10022",
  phone: "+16468993079",
  email: "Alain.Onesti@elliman.com",
  schedulingUrl: "https://calendly.com/aonesti-elliman",
  photoUrl: "/agents/alain-onesti.jpg",
  storyHeadline: "A more analytical approach to selling real estate.",
  story:
    "My background is unusual for residential brokerage.\n\nBefore real estate, I spent more than two decades in technology and executive leadership. I now combine that analytical discipline with Douglas Elliman's market reach and the experience of a team with more than $2 billion in Manhattan sales.\n\nFor a seller, that means the strategy starts with evidence — not with choosing a price designed simply to win the listing.\n\nI want to understand where buyers are likely to see value, position the property accordingly, and then use real market response to make disciplined decisions with you.",
  credentials: [
    "Over $2B in Manhattan real estate sales",
    "Technology + analytical discipline",
    "Douglas Elliman",
  ],
};

/**
 * The Galleria · Residence 33B
 * Authoritative content from the verified Seller Analysis data package.
 * No fabricated renovation specs. Owner name unknown → property-centric copy.
 */
export const galleria33b: SellerAnalysis = {
  slug: "g33b",
  templateType: "seller_analysis",
  noindex: true,
  scrollCue: "See the analysis",
  agent: ALAIN,
  hero: {
    eyebrow: "THE GALLERIA · RESIDENCE 33B",
    headline: "A closer look at what your home could command today.",
    body: [
      "I reviewed the recent sales inside The Galleria, with particular attention to renovated apartments and same-line residences, to understand where 33B sits in today's market.",
      "My preliminary conclusion is that the renovation supports a premium — but the recent sales also show how much the initial positioning can influence the final result.",
    ],
    callout:
      "My objective isn't simply to suggest the highest asking price. It's to identify the position that gives us the strongest path to the best achievable outcome.",
  },
  property: {
    address: "117 East 57th Street",
    unit: "33B",
    building: "The Galleria",
    neighborhood: "Midtown East",
    city: "Manhattan",
    propertyType: "Condominium",
    bedrooms: 2,
    bathrooms: 2,
    squareFeet: 1433,
    condition: "Fully renovated — confirmed by client",
    purchasePrice: 2333333,
    purchaseDate: "July 2014",
    notes:
      "South and west exposures. Current layout and renovation specifications have not yet been independently reviewed — this analysis treats the renovation as confirmed by the owner, without inventing finishes or scope.",
    images: [
      {
        url: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1800&q=80",
        alt: "Manhattan skyline — Midtown context for The Galleria",
        caption: "Midtown Manhattan",
      },
      {
        url: "https://images.unsplash.com/photo-1449844908441-8829872d2607?auto=format&fit=crop&w=1400&q=80",
        alt: "High-rise residential context in Manhattan",
        caption: "Residential context",
      },
    ],
  },
  valuation: {
    low: 2180000,
    high: 2400000,
    midpoint: 2280000,
    suggestedAsk: 2450000,
    rangeLabel: "Estimated sale range",
    midpointLabel: "Central indication",
    suggestedAskLabel: "Suggested launch",
    ppsfLow: 1521,
    ppsfHigh: 1675,
    ppsfMid: 1591,
    ppsfAsk: 1710,
    narrative:
      "The distinction between value and launch strategy matters. My current analysis points to approximately $2.28M as the central indication, while I would initially test the market at $2.45M. That gives the apartment room to capture upside without assuming that the strongest comparable can simply be applied dollar-for-dollar to 33B.",
    followUp:
      "This is a preliminary in-building comparative market analysis — not an appraisal.",
  },
  compsIntro:
    "The two most recent renovated sales effectively establish the outer reference points. 33B's central indication sits between them rather than simply matching the highest $/sqft achieved in the building. Same-line B apartments then provide an additional reality check for size, layout lineage and historical buyer behavior.",
  comps: [
    {
      id: "35H",
      unit: "35H",
      group: "Primary renovated benchmarks · 2026",
      salePrice: 2300000,
      initialAsk: 2350000,
      listPrice: 2350000,
      discountLabel: "2.1%",
      saleDate: "2026",
      daysOnMarket: 45,
      sqft: 1209,
      ppsf: 1902,
      floor: 35,
      condition: "Renovated",
      saleType: "Upper benchmark",
      whyItMatters:
        "The strongest recent upper benchmark. It achieved the highest relevant $/sqft, but it is smaller than 33B and a different line — so I would not simply extrapolate $1,902/sqft to 33B.",
    },
    {
      id: "35D",
      unit: "35D",
      group: "Primary renovated benchmarks · 2026",
      salePrice: 1700000,
      initialAsk: 1950000,
      listPrice: 1800000,
      discountLabel: "12.8% from initial ask",
      saleDate: "2026",
      daysOnMarket: 358,
      sqft: 1224,
      ppsf: 1389,
      floor: 35,
      condition: "Renovated convertible 2BR",
      saleType: "Lower benchmark",
      whyItMatters:
        "Useful recent renovated lower benchmark (sale-area basis 1,224 sqft). Also shows that renovation alone does not guarantee pricing power if initial positioning and buyer response are not aligned.",
    },
    {
      id: "27B",
      unit: "27B",
      group: "Same-line B residences",
      salePrice: 1700000,
      saleDate: "2024",
      sqft: 1423,
      ppsf: 1195,
      floor: 27,
      condition: "Not verified",
      saleType: "Same-line evidence",
      whyItMatters:
        "Highly relevant same-line / similar-size transaction. Condition was not verified, so I treat it as structural evidence rather than a direct renovated-value benchmark.",
    },
    {
      id: "31B",
      unit: "31B",
      group: "Same-line B residences",
      salePrice: 1900000,
      initialAsk: 2050000,
      listPrice: 1995000,
      discountLabel: "7.3%",
      saleDate: "2020",
      daysOnMarket: 385,
      sqft: 1423,
      ppsf: 1335,
      floor: 31,
      condition: "Renovated at time of sale",
      saleType: "Same-line renovated",
      whyItMatters:
        "Relevant same-line renovated evidence, but substantially older than the primary 2026 transactions.",
    },
    {
      id: "28B",
      unit: "28B",
      group: "Same-line B residences",
      salePrice: 1425000,
      initialAsk: 1495000,
      listPrice: 1495000,
      discountLabel: "4.7%",
      saleDate: "2022",
      daysOnMarket: 33,
      sqft: 1334,
      ppsf: 1068,
      floor: 28,
      condition: "Required renovation",
      saleType: "Condition reference",
      whyItMatters:
        "Same-line lower reference and useful evidence of the effect of condition.",
    },
    {
      id: "21CD",
      unit: "21CD",
      group: "Secondary reference",
      salePrice: 1911000,
      saleDate: "2025",
      daysOnMarket: 506,
      sqft: 1800,
      ppsf: 1062,
      floor: 21,
      condition: "Larger combined apartment",
      saleType: "Secondary only",
      whyItMatters:
        "Larger combined apartment on a lower floor — secondary reference only.",
    },
  ],
  methodologyIntro: {
    overline: "How I read the building",
    headline: "33B deserves to be evaluated differently from an average sale.",
  },
  methodology: [
    {
      title: "Why a simple average fails here",
      intro:
        "At approximately 1,433 square feet, with two bedrooms, two bathrooms, south and west exposures and a full renovation, 33B has several characteristics that support premium positioning. But recent sales inside The Galleria span a very wide range — from approximately $1,389/sqft for renovated 35D to $1,902/sqft for renovated 35H. That spread is precisely why I would not value 33B by applying a simple building average.",
      rows: [
        {
          label: "Renovated 35H — upper benchmark",
          value: "$1,902 / sqft",
          note: "$2.30M · 1,209 sqft · 45 DOM · 2.1% discount",
          kind: "fact",
        },
        {
          label: "Renovated 35D — lower benchmark",
          value: "$1,389 / sqft",
          note: "$1.70M · 1,224 sqft sale basis · 358 DOM · 12.8% discount",
          kind: "fact",
        },
        {
          label: "33B central indication",
          value: "$1,591 / sqft",
          note: "≈ $2.28M on 1,433 sqft",
          kind: "calculation",
        },
      ],
      result: {
        label: "Positioning relative to the 2026 renovated pair",
        value: "$2.18M – $2.40M",
        note: "Narrower than simply applying the highest observed $/sqft to 33B",
      },
    },
    {
      title: "Reference relationships at $2.28M",
      intro:
        "These percentages are reference comparisons. They are not statistically measured renovation premiums, and they are not formal time adjustments.",
      rows: [
        {
          label: "vs 35H at $1,902 / sqft",
          value: "≈ 16% below",
          kind: "calculation",
        },
        {
          label: "vs 35D at $1,389 / sqft (1,224 sqft basis)",
          value: "≈ 15% above",
          kind: "calculation",
        },
        {
          label: "vs renovated 31B at $1,335 / sqft",
          value: "≈ 19% above",
          note: "Historical same-line renovated evidence",
          kind: "calculation",
        },
      ],
      result: {
        label: "Central indication",
        value: "≈ $2.28M",
        note: "$1,591 / sqft",
      },
    },
    {
      title: "The opportunity is not just in the price",
      intro:
        "Both 35H and 35D provide renovated-apartment evidence, yet their market outcomes were dramatically different. That doesn't tell us that one strategy alone caused the difference — the apartments are not identical. What it does tell us is that condition alone does not determine the outcome. The relationship between the apartment, its presentation, its initial price and buyer expectations matters.",
      rows: [
        {
          label: "35H outcome",
          value: "$2.30M · 45 DOM · 2.1% discount",
          kind: "fact",
        },
        {
          label: "35D outcome",
          value: "$1.70M · 358 DOM · 12.8% discount",
          kind: "fact",
        },
      ],
      result: {
        label: "Seller insight",
        value: "Positioning drives the result",
        note: "Renovation matters — but so does the initial relationship between price and buyer expectations",
      },
    },
    {
      title: "Building median $/sqft — limited sample",
      intro:
        "Annual medians from the supplied sample can help set context, but they must be read carefully. Do not treat 2026 as a dramatic building-wide appreciation story — that year's median rests on only two materially different sales, so the apparent increase is largely a mix effect.",
      rows: [
        { label: "2019", value: "$1,304", note: "5 sales", kind: "fact" },
        { label: "2020", value: "$1,335", note: "1 sale", kind: "fact" },
        { label: "2021", value: "$1,120", note: "3 sales", kind: "fact" },
        { label: "2022", value: "$1,325", note: "3 sales", kind: "fact" },
        { label: "2023", value: "$1,263", note: "2 sales", kind: "fact" },
        { label: "2024", value: "$1,195", note: "1 sale", kind: "fact" },
        { label: "2025", value: "$1,062", note: "1 sale", kind: "fact" },
        { label: "2026 YTD", value: "$1,646", note: "2 sales — mix effect", kind: "interpretation" },
      ],
      result: {
        label: "How I use this",
        value: "Context only",
        note: "Valuation is driven by the renovated pair + same-line evidence, not by the annual median",
      },
    },
  ],
  strategyIntro: {
    overline: "Recommended positioning",
    headline: "Start strong. Then let the market give us information.",
  },
  strategy: [
    {
      key: "launch",
      label: "Launch",
      body:
        "Launch around $2.45M based on the current analysis, while preserving flexibility as new evidence emerges. The purpose of that ask is not to claim the apartment is worth $2.45M — it is an initial market-positioning strategy designed to protect upside, present the renovation appropriately, create a disciplined starting point, and collect qualified buyer response.",
    },
    {
      key: "present",
      label: "Present",
      body:
        "Build the marketing around the renovation, scale, exposures and the qualities that differentiate 33B from the broader building inventory — without inventing finishes we have not yet inspected.",
    },
    {
      key: "activate",
      label: "Activate",
      body:
        "Launch simultaneously to the public market and the brokerage community, with direct outreach to agents actively working with qualified buyers in this price segment.",
    },
    {
      key: "read_adapt",
      label: "Read & Adapt",
      body:
        "For the first 3–4 weeks, measure the quality of response rather than reacting to isolated comments. Track qualified showings, repeat interest, broker feedback, objections and offers. If the evidence tells us the market sees the apartment differently, adjust deliberately — not emotionally and not automatically.",
    },
  ],
  timelineIntro: {
    overline: "How I would launch",
    headline: "A disciplined opening — then evidence-based decisions.",
  },
  timeline: [
    {
      number: "01",
      label: "Position",
      detail: "Launch around $2.45M while preserving flexibility as new evidence emerges.",
    },
    {
      number: "02",
      label: "Present",
      detail: "Lead with renovation, scale, exposures and what differentiates 33B.",
    },
    {
      number: "03",
      label: "Activate",
      detail: "Public market + brokerage outreach to qualified buyers in this segment.",
    },
    {
      number: "04",
      label: "Read the market",
      detail: "First 3–4 weeks: quality of response, not isolated comments.",
    },
    {
      number: "05",
      label: "Adapt",
      detail: "Adjust deliberately if warranted — never automatically.",
    },
  ],
  recommendation: {
    body:
      "33B's full renovation supports positioning toward the upper end of the relevant in-building evidence — but renovation alone does not justify treating it as equivalent to the highest-priced renovated comparable. I would reconcile the recent renovated 35H sale as an upper benchmark, renovated 35D as a lower benchmark, and same-line B apartments for layout and size relevance, with older transactions as secondary historical context. That produces an intentionally narrower $2.18M–$2.40M estimated sale range, with a current central indication near $2.28M and a suggested launch of $2.45M. Before finalizing the launch strategy, I would personally review the apartment, renovation quality, current layout, carrying costs, assessments and competing inventory. Those details may refine the final recommendation — and that is exactly how a serious seller process should work.",
  },
  contact: {
    headline: "Let's see the apartment together.",
    body:
      "The analysis gives me a strong starting point. The next step is much simpler: I'd like to see 33B in person, understand the renovation and what matters most to you in a potential sale. From there, I can refine the valuation and show you exactly how I would bring the apartment to market.",
  },
};

export const sellerAnalysisPresentations: SellerAnalysis[] = [galleria33b];

export interface SADemoEntry {
  id: string;
  label: string;
  description: string;
  data: SellerAnalysis;
}

export const sellerAnalysisDemos: SADemoEntry[] = [
  {
    id: "sa-galleria-33b",
    label: "Analysis · Galleria 33B",
    description: "The Galleria · 117 East 57th Street · Residence 33B",
    data: galleria33b,
  },
];

export function getSellerAnalysisBySlug(slug?: string): SellerAnalysis | undefined {
  if (!slug) return undefined;
  return sellerAnalysisPresentations.find((p) => p.slug === slug);
}
